import { ChangeEvent, useCallback, useMemo, useState } from 'react';
import { noop } from '@cbhq/cds-utils';
import { Button } from '@cbhq/cds-web/buttons';
import { NativeTextArea, Select, SelectOption, TextInput } from '@cbhq/cds-web/controls';
import { VStack } from '@cbhq/cds-web/layout';
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@cbhq/cds-web/overlays';
import { TextBody, TextLabel1 } from '@cbhq/cds-web/typography';

import useWebhooksURLValidation from ':cloud/hooks/useWebhooksURLValidation';
import { CoinbaseWebhook } from ':cloud/types/ts_types';
import { WebhookFiltersTitle } from ':cloud/widgets/webhooks/components/WebhookFiltersTitle';
import { getAddressEntity, getEventTypeFilters } from ':cloud/widgets/webhooks/helpers';

import {
  eventTypesMap,
  getEventType,
  getEventTypeProps,
  getNetwork,
  networksMap,
  validateEthereumAddress,
} from '../helpers';

type WebhookCreateModalProps = {
  onRequestClose?: () => void;
  onSubmit: (webhook: CoinbaseWebhook) => void;
  enableAdvancedSettings?: boolean;
};

const eventTypeMapping = Object.fromEntries(
  Object.entries(eventTypesMap).map(([key, value]) => [value, key]),
);

export const addressTooltipContent =
  "All three address fields use 'AND' logic. If 'From' and 'To' are both set to 0xA, only transactions from 0xA to 0xA will be captured.";

export function CoinbaseWebhookCreateModal({ onRequestClose, onSubmit }: WebhookCreateModalProps) {
  const networks = Object.keys(networksMap);

  const [webhookURL, setWebhookURL] = useState('');
  const [contractAddress, setContractAddress] = useState('');
  const [apiSecret, setAPISecret] = useState('');
  const [fromAddress, setFromAddress] = useState('');
  const [toAddress, setToAddress] = useState('');
  const [selectedNetwork, setSelectedNetwork] = useState('');
  const [selectedEvent, setSelectedEvent] = useState('');
  const [contractAddressError, setContractAddressError] = useState('');
  const [apiSecretError, setAPISecretError] = useState('');
  const [fromAddressError, setFromAddressError] = useState('');
  const [toAddressError, setToAddressError] = useState('');
  const [rawAddresses, setRawAddresses] = useState('');
  const [addresses, setAddresses] = useState<string[]>([]);
  const [addressesError, setAddressesError] = useState('');

  const { urlError, validateUrl } = useWebhooksURLValidation();
  const { isWalletActivity, isSmartContractEvents } = getEventTypeProps(selectedEvent);

  const handleOnChangeURL = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const url = event.target.value.trim();
      setWebhookURL(url);
      validateUrl(url);
    },
    [validateUrl],
  );

  const handleNetworkChange = useCallback(
    (network: string) => {
      setSelectedNetwork(network);
    },
    [setSelectedNetwork],
  );

  const handleEventChange = useCallback(
    (event: string) => {
      setSelectedEvent(eventTypeMapping[event]);
    },
    [setSelectedEvent],
  );

  const handleOnChangeApiSecret = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const secret = event.target.value.trim();
    setAPISecret(secret);
    if (secret.length > 64) {
      setAPISecretError('Signature must be less than 64 characters');
      return;
    }

    if (!secret) {
      setAPISecretError('Please enter a valid signature for your webhook');
    } else {
      setAPISecretError('');
    }
  }, []);

  const handleOnChangeContractAddress = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const address = event.target.value.trim();
      setContractAddress(address);
      if (address && !validateEthereumAddress(address)) {
        setContractAddressError(
          'Please enter a valid Ethereum address (40 hexadecimal characters starting with 0x)',
        );
      } else {
        setContractAddressError('');
      }
    },
    [],
  );

  const handleOnChangeFromAddress = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const address = event.target.value.trim();
    setFromAddress(address);
    if (address && !validateEthereumAddress(address)) {
      setFromAddressError(
        'Please enter a valid Ethereum address (40 hexadecimal characters starting with 0x)',
      );
    } else {
      setFromAddressError('');
    }
  }, []);

  const handleOnChangeToAddress = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const address = event.target.value.trim();
    setToAddress(address);
    if (address && !validateEthereumAddress(address)) {
      setToAddressError(
        'Please enter a valid Ethereum address (40 hexadecimal characters starting with 0x)',
      );
    } else {
      setToAddressError('');
    }
  }, []);

  const handleOnChangeAddresses = useCallback((event: ChangeEvent<HTMLTextAreaElement>) => {
    const newAddressesRaw = event.target.value.trim();
    setRawAddresses(newAddressesRaw);

    // validate addresses
    let newAddresses = newAddressesRaw.split(',');
    newAddresses = newAddresses.filter((addr) => addr.length > 0);
    for (const addr of newAddresses) {
      if (!validateEthereumAddress(addr)) {
        setAddressesError('One or more addresses entered are invalid.');
        break;
      } else {
        setAddressesError('');
      }
    }
    setAddresses(newAddresses);
  }, []);

  const handleSubmit = useCallback(() => {
    validateUrl(webhookURL);

    if (urlError) {
      return;
    }
    const coinbaseWebhook: CoinbaseWebhook = {
      notificationHeaders: [
        {
          key: 'x-webhook-signature',
          value: apiSecret,
        },
      ],
      notificationUri: webhookURL,
      network: selectedNetwork,
      eventType: selectedEvent,
      ...getEventTypeFilters({
        isWalletActivity,
        isSmartContractEvents,
        addresses,
        contractAddress,
        fromAddress,
        toAddress,
      }),
    };
    onSubmit(coinbaseWebhook);
  }, [
    apiSecret,
    webhookURL,
    urlError,
    selectedNetwork,
    selectedEvent,
    contractAddress,
    fromAddress,
    toAddress,
    onSubmit,
    validateUrl,
    addresses,
    isWalletActivity,
    isSmartContractEvents,
  ]);

  const apiSecretTooltipContent =
    'This value will be sent in the `x-webhook-signature` header on callbacks from Coinbase.';

  const filtersContent = useMemo(() => {
    if (!selectedEvent) {
      return <TextBody as="p">Please select event type.</TextBody>;
    }
    if (isWalletActivity || isSmartContractEvents) {
      const placeholder = `Enter the ${getAddressEntity(
        isSmartContractEvents,
      ).toLowerCase()} addresses here`;
      const label = `${getAddressEntity(isSmartContractEvents)} addresses (separated by comma)`;

      let addressesCount = 0;
      if (addresses?.length && typeof addresses?.length === 'number') {
        addressesCount = addresses.length;
      }
      return (
        <TextInput
          inputNode={
            <NativeTextArea
              value={rawAddresses}
              onChange={handleOnChangeAddresses}
              rows={5}
              cols={5}
              placeholder={placeholder}
            />
          }
          helperText={addressesError || `${addressesCount} address(es)`}
          label={label}
          variant={addressesError ? 'negative' : undefined}
        />
      );
    }
    return (
      <>
        <TextLabel1 as="p">
          {addressTooltipContent}
          <br />
          At least one of the following addresses is required to create a webhook.
        </TextLabel1>
        <TextInput
          label="Contract Address"
          placeholder="0xdeadbeef"
          value={contractAddress}
          onChange={handleOnChangeContractAddress}
          helperText={contractAddressError}
          variant={contractAddressError ? 'negative' : undefined}
        />
        <TextInput
          label="From Address"
          placeholder="0xdeadbeef"
          value={fromAddress}
          onChange={handleOnChangeFromAddress}
          helperText={fromAddressError}
          variant={fromAddressError ? 'negative' : undefined}
        />
        <TextInput
          label="To Address"
          placeholder="0xdeadbeef"
          value={toAddress}
          onChange={handleOnChangeToAddress}
          helperText={toAddressError}
          variant={toAddressError ? 'negative' : undefined}
        />
      </>
    );
  }, [
    selectedEvent,
    isWalletActivity,
    isSmartContractEvents,
    contractAddress,
    handleOnChangeContractAddress,
    contractAddressError,
    fromAddress,
    handleOnChangeFromAddress,
    fromAddressError,
    toAddress,
    handleOnChangeToAddress,
    toAddressError,
    addresses.length,
    rawAddresses,
    handleOnChangeAddresses,
    addressesError,
  ]);

  return (
    <Modal onRequestClose={onRequestClose || noop} visible width={480}>
      <ModalHeader title="Create Webhook" />
      <ModalBody>
        <VStack gap={2}>
          <TextInput
            label="Webhook URL"
            required
            placeholder="https://example.com/"
            value={webhookURL}
            onChange={handleOnChangeURL}
            helperText={urlError}
            variant={urlError ? 'negative' : undefined}
          />
          <Select
            value={getNetwork(selectedNetwork)}
            label="Network Type"
            placeholder="Select Network Type"
            onChange={handleNetworkChange}
          >
            {networks.map((option) => (
              <SelectOption value={option} key={option} title={getNetwork(option)} />
            ))}
          </Select>
          <Select
            value={getEventType(selectedEvent)}
            label="Event Type"
            placeholder="Select Event Type"
            onChange={handleEventChange}
          >
            {Object.values(eventTypesMap).map((eventType) => (
              <SelectOption value={eventType} key={eventType} title={eventType} />
            ))}
          </Select>
          <TextInput
            label="Webhook Signature"
            placeholder="Your Webhook Signature"
            value={apiSecret}
            onChange={handleOnChangeApiSecret}
            helperText={apiSecretError || apiSecretTooltipContent}
            variant={apiSecretError ? 'negative' : undefined}
          />
          <WebhookFiltersTitle />
          {filtersContent}
        </VStack>
      </ModalBody>
      <ModalFooter
        secondaryAction={
          <Button variant="secondary" onPress={onRequestClose}>
            Cancel
          </Button>
        }
        primaryAction={
          <Button
            type="submit"
            onPress={handleSubmit}
            testID="submit"
            disabled={
              !webhookURL ||
              !selectedNetwork ||
              !selectedEvent ||
              (!contractAddress && !fromAddress && !toAddress && !addresses?.length)
            }
          >
            Create
          </Button>
        }
      />
    </Modal>
  );
}
